import { type FloatingPanelGeometry, parseFloatingPanelGeometry } from "@/lib/local-ui/floating-panel-geometry";
import { DEFAULT_FLUID_TEXT_ZOOM, normalizeFluidTextZoom } from "@/lib/local-ui/fluid-text-zoom";
import { DEFAULT_SITE_ZOOM } from "@/lib/local-ui/site-zoom";
import { isJsonObjectStructurally, type JsonObject, type JsonValue } from "@/lib/serialization/json-boundary";
import { isRecord } from "@/utils/is-record";

/** The cards that fold, each remembered per browser. Only the Dashboard's widgets fold (2026-10-26; the side rails'
 *  cards stay open and resize instead); every other card is always open. All open by default. */
const DEFAULT_CLIENT_CARDS = {
    /* ── The contact page's rail itself (2026-10-15), which folds to a strip of its cards' icons and gives the content
       column its width. */
    rail: true,
    /* ── The dashboard's grid widgets (ruled 2026-09-16). A closed widget's cell shrinks to the grid's smallest
       tier — see SortableWidgetGrid. */
    dashboardClientStatus: true,
    dashboardKeyFigures: true,
    dashboardCash: true,
    dashboardPace: true,
    dashboardUnpaid: true,
    dashboardFlows: true,
    dashboardBridge: true,
    dashboardCalendar: true,
    /** The portal rail's Status card (2026-10-12). */
    portalStatus: true,
};

/** The Lifecycle card's row folds, remembered like the cards' own (ruled
 *  2026-09-15). */
const DEFAULT_LIFECYCLE_ROWS = {
    engagement: false,
    authorization: false,
    portal: false,
    billing: false,
    service: false,
};
export type LifecycleRowKey = keyof typeof DEFAULT_LIFECYCLE_ROWS;


export type ClientCardId = keyof typeof DEFAULT_CLIENT_CARDS;

/** A side rail's size, remembered per browser (2026-10-26): its width and its cards' shares of its height, each null
 *  until the member drags that handle. Unsized, a rail is 400px and its cards stand at their own heights, the last
 *  taking what is left. */
export type SideRailLayout = { width: number | null; panes: number[] | null };
/** The rails: the contact page's, the Organization page's Setup rail, the Plan & Usage page's Billing rail, the
 *  account page's Profile rail. */
export type SideRailKey = "contact" | "organization" | "billing" | "account";
const DEFAULT_SIDE_RAILS: Record<SideRailKey, SideRailLayout> = {
    contact: { width: null, panes: null },
    organization: { width: null, panes: null },
    billing: { width: null, panes: null },
    account: { width: null, panes: null },
};

export type LocalUiSettingsV1 = { version: 1; updatedAt: number; appearance: { mode: "light" | "dark" | "system"; crmTheme: string; zoom: number; fluidTextZoom: number; customThemeCss?: string; }; crmShell: { sidebarOpen: boolean; }; tables: { buttonLabels: boolean; autoWidth: boolean; hideEmptyOptions: boolean; showArchived: boolean; }; fluid: { quickChatGeometry: FloatingPanelGeometry | null; }; roundWorkspace: { sectionOpen: { strategy: boolean; report: boolean; changes: boolean; select: boolean; selectWorklist: boolean; letters: boolean; delivery: boolean; }; }; clientCards: typeof DEFAULT_CLIENT_CARDS; lifecycleRows: typeof DEFAULT_LIFECYCLE_ROWS; sideRails: Record<SideRailKey, SideRailLayout>; dashboard?: JsonObject; };

export const LOCAL_UI_SETTINGS_KEY = "disputewell:ui-settings:v1";

export const DEFAULT_LOCAL_UI_SETTINGS: LocalUiSettingsV1 = { version: 1, updatedAt: 0, appearance: { mode: "system", crmTheme: "default", zoom: DEFAULT_SITE_ZOOM, fluidTextZoom: DEFAULT_FLUID_TEXT_ZOOM }, crmShell: { sidebarOpen: true }, tables: { buttonLabels: false, autoWidth: false, hideEmptyOptions: false, showArchived: false }, fluid: { quickChatGeometry: null }, roundWorkspace: { sectionOpen: { strategy: false, report: false, changes: false, select: false, selectWorklist: false, letters: false, delivery: false } }, clientCards: { ...DEFAULT_CLIENT_CARDS }, lifecycleRows: { ...DEFAULT_LIFECYCLE_ROWS }, sideRails: { ...DEFAULT_SIDE_RAILS } };

type LocalUiSettingsPathValueMap = { "appearance.mode": LocalUiSettingsV1["appearance"]["mode"]; "appearance.crmTheme": string; "appearance.zoom": number; "appearance.fluidTextZoom": number; "appearance.customThemeCss": string | undefined; "crmShell.sidebarOpen": boolean; "tables.buttonLabels": boolean; "tables.autoWidth": boolean; "tables.hideEmptyOptions": boolean; "tables.showArchived": boolean; "fluid.quickChatGeometry": FloatingPanelGeometry | null; "roundWorkspace.sectionOpen.strategy": boolean; "roundWorkspace.sectionOpen.report": boolean; "roundWorkspace.sectionOpen.changes": boolean; "roundWorkspace.sectionOpen.select": boolean; "roundWorkspace.sectionOpen.selectWorklist": boolean; "roundWorkspace.sectionOpen.letters": boolean; "roundWorkspace.sectionOpen.delivery": boolean; } & { [K in ClientCardId as `clientCards.${K}`]: boolean } & { [K in LifecycleRowKey as `lifecycleRows.${K}`]: boolean } & { [K in SideRailKey as `sideRails.${K}`]: SideRailLayout };

export type LocalUiSettingsPath = keyof LocalUiSettingsPathValueMap;
export type LocalUiSettingsPathValue<P extends LocalUiSettingsPath> = LocalUiSettingsPathValueMap[P];

function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === "boolean" ? value : fallback;
}

function asFiniteNumber(value: unknown, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback: string): string {
    return typeof value === "string" ? value : fallback;
}

function asMode(value: unknown): LocalUiSettingsV1["appearance"]["mode"] {
    return value === "light" || value === "dark" || value === "system" ? value : DEFAULT_LOCAL_UI_SETTINGS.appearance.mode;
}

function asJsonValue(value: unknown): JsonValue | undefined {
    if (value === null) {
        return null;
    }
    if (typeof value === "string" || typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }

    if (Array.isArray(value)) {
        return value.map((item) => asJsonValue(item) ?? null);
    }

    if (!isRecord(value)) {
        return undefined;
    }

    const result: JsonObject = {};
    for (const [key, item] of Object.entries(value)) {
        const parsed = asJsonValue(item);
        if (parsed !== undefined) {
            result[key] = parsed;
        }
    }
    return result;
}

/** A stored rail size, or the rail's default where a value is not a positive size. */
function asSideRailLayout(value: unknown): SideRailLayout {
    const layout = isRecord(value) ? value : {};
    const positive = (entry: unknown): entry is number => typeof entry === "number" && Number.isFinite(entry) && entry > 0;
    const panes = Array.isArray(layout.panes) && layout.panes.length > 0 && layout.panes.every(positive) ? layout.panes : null;
    const width = positive(layout.width) ? layout.width : null;
    // A stored value already in shape is kept as is, so a write elsewhere in the settings does not wake a rail.
    return layout.panes === panes && layout.width === width && Object.keys(layout).length === 2 ? (layout as SideRailLayout) : { width, panes };
}

function asJsonObject(value: unknown): JsonObject | undefined {
    const parsed = asJsonValue(value);
    return isJsonObjectStructurally(parsed) ? parsed : undefined;
}

export function parseLocalUiSettingsV1(raw: unknown): LocalUiSettingsV1 | null {
    if (!isRecord(raw)) {
        return null;
    }
    if (raw.version !== 1) {
        return null;
    }

    const defaults = DEFAULT_LOCAL_UI_SETTINGS;
    const appearance = isRecord(raw.appearance) ? raw.appearance : {};
    const crmShell = isRecord(raw.crmShell) ? raw.crmShell : {};
    const fluid = isRecord(raw.fluid) ? raw.fluid : {};
    const tables = isRecord(raw.tables) ? raw.tables : {};
    const roundWorkspace = isRecord(raw.roundWorkspace) ? raw.roundWorkspace : {};
    const roundWorkspaceSectionOpen = isRecord(roundWorkspace.sectionOpen) ? roundWorkspace.sectionOpen : {};

    const rawClientCards = isRecord(raw.clientCards) ? raw.clientCards : {};
    const clientCards = { ...DEFAULT_CLIENT_CARDS };
    for (const key of Object.keys(clientCards) as ClientCardId[]) {
        clientCards[key] = asBoolean(rawClientCards[key], DEFAULT_CLIENT_CARDS[key]);
    }

    const rawLifecycleRows = isRecord(raw.lifecycleRows) ? raw.lifecycleRows : {};
    const lifecycleRows = { ...DEFAULT_LIFECYCLE_ROWS };
    for (const key of Object.keys(lifecycleRows) as LifecycleRowKey[]) {
        lifecycleRows[key] = asBoolean(rawLifecycleRows[key], DEFAULT_LIFECYCLE_ROWS[key]);
    }

    const rawSideRails = isRecord(raw.sideRails) ? raw.sideRails : {};
    const sideRails = { ...DEFAULT_SIDE_RAILS };
    for (const key of Object.keys(sideRails) as SideRailKey[]) {
        sideRails[key] = asSideRailLayout(rawSideRails[key]);
    }
    const result: LocalUiSettingsV1 = { clientCards, lifecycleRows, sideRails, version: 1, updatedAt: asFiniteNumber(raw.updatedAt, defaults.updatedAt), appearance: { mode: asMode(appearance.mode), crmTheme: asString(appearance.crmTheme, defaults.appearance.crmTheme), zoom: asFiniteNumber(appearance.zoom, defaults.appearance.zoom), fluidTextZoom: normalizeFluidTextZoom(appearance.fluidTextZoom) }, crmShell: { sidebarOpen: asBoolean(crmShell.sidebarOpen, defaults.crmShell.sidebarOpen) }, tables: { buttonLabels: asBoolean(tables.buttonLabels, defaults.tables.buttonLabels), autoWidth: asBoolean(tables.autoWidth, defaults.tables.autoWidth), hideEmptyOptions: asBoolean(tables.hideEmptyOptions, defaults.tables.hideEmptyOptions), showArchived: asBoolean(tables.showArchived, defaults.tables.showArchived) }, fluid: { quickChatGeometry: parseFloatingPanelGeometry(fluid.quickChatGeometry) }, roundWorkspace: { sectionOpen: { strategy: asBoolean(roundWorkspaceSectionOpen.strategy, defaults.roundWorkspace.sectionOpen.strategy), report: asBoolean(roundWorkspaceSectionOpen.report, defaults.roundWorkspace.sectionOpen.report), changes: asBoolean(roundWorkspaceSectionOpen.changes, defaults.roundWorkspace.sectionOpen.changes), select: asBoolean(roundWorkspaceSectionOpen.select, defaults.roundWorkspace.sectionOpen.select), selectWorklist: asBoolean(roundWorkspaceSectionOpen.selectWorklist, defaults.roundWorkspace.sectionOpen.selectWorklist), letters: asBoolean(roundWorkspaceSectionOpen.letters, defaults.roundWorkspace.sectionOpen.letters), delivery: asBoolean(roundWorkspaceSectionOpen.delivery, defaults.roundWorkspace.sectionOpen.delivery) } } };

    if (typeof appearance.customThemeCss === "string") {
        result.appearance.customThemeCss = appearance.customThemeCss;
    }

    const dashboard = asJsonObject(raw.dashboard);
    if (dashboard) {
        result.dashboard = dashboard;
    }

    return result;
}
